import type { ResourceBundle } from "@/components/smartFormik/SmartFormik";
import type { RecursiveErrorNode } from "@/utils/serverErrors";

import { getIn, type FormikErrors, type FormikValues } from "formik";
import i18next from "i18next";

/**
 * Recursively searches through a ResourceBundle i18next object to find a key with a matching value.
 * If a prefix is provided, it will be prepended to each key in the search.
 * @param {ResourceBundle} obj - The ResourceBundle object to search through.
 * @param {string} value - The value to search for.
 * @param {string} [prefix] - The prefix to prepend to each key in the search.
 * @returns {string | undefined} The key with a matching value, or undefined if no match is found.
 */
export function findKeyByValue(
  obj: ResourceBundle,
  value: string
): string | undefined {
  for (const [key, val] of Object.entries(obj)) {
    if (val === value) {
      return key;
    }

    if (typeof val === "object" && val !== null) {
      const found = findKeyByValue(val as ResourceBundle, value);
      if (found) {
        return `${key}.${found}`;
      }
    }
  }
  return undefined;
}

/**
 * Translate Formik errors using a ResourceBundle object.
 * Recursively handles nested objects and arrays.
 *
 * @param {FormikErrors<T>} currentErrors - Formik errors to translate.
 * @param {ResourceBundle} oldErrorsBundle - The ResourceBundle object to search through.
 * @param {string} namespace - The namespace to use when translating errors.
 * @returns {FormikErrors<T>} The translated Formik errors.
 */
export function translateErrors<T extends FormikValues>(
  currentErrors: FormikErrors<T>,
  oldErrorsBundle: ResourceBundle,
  namespace: string
): FormikErrors<T> {
  return translateRecursive(
    currentErrors,
    oldErrorsBundle,
    namespace
  ) as FormikErrors<T>;
}

/**
 * Internal function for recursively traversing any level of error nesting.
 */
function translateRecursive(
  node: RecursiveErrorNode,
  oldErrorsBundle: ResourceBundle,
  namespace: string
): RecursiveErrorNode {
  if (typeof node === "string") {
    const key = findKeyByValue(oldErrorsBundle, node);
    if (key) {
      return i18next.t(`${namespace}:${key}`);
    }
    return node;
  }

  if (Array.isArray(node)) {
    return node.map((item) =>
      translateRecursive(item, oldErrorsBundle, namespace)
    );
  }

  if (typeof node === "object" && node !== null) {
    const next: Record<string, RecursiveErrorNode> = {};
    for (const [key, value] of Object.entries(node)) {
      next[key] = translateRecursive(
        value as RecursiveErrorNode,
        oldErrorsBundle,
        namespace
      );
    }
    return next;
  }

  return node;
}

/**
 * Helper for recursively sets a value in an object to undefined via a deep path (e.g. "phones[0].number")
 *
 * @param {Record<string, RecursiveErrorNode>} obj - The object to set the key in.
 * @param {string} path - The path to set, separated by dots or array indices.
 */
export function unsetDeep(
  obj: Record<string, RecursiveErrorNode>,
  path: string
): void {
  const keys = path.split(/[.[\]]+/).filter(Boolean);

  const run = (current: RecursiveErrorNode, index: number): boolean => {
    const key = keys[index];

    if (!current || typeof current !== "object" || !(key in current)) {
      return false;
    }

    const currentAsRecord = current as Record<string, RecursiveErrorNode>;

    if (index === keys.length - 1) {
      if (Array.isArray(currentAsRecord)) {
        currentAsRecord[Number(key)] = undefined;
      } else {
        delete currentAsRecord[key];
      }
    } else {
      run(currentAsRecord[key], index + 1);
    }

    const child = currentAsRecord[key];

    const isArrayEmpty =
      Array.isArray(child) && child.every((item) => item === undefined);
    const isObjectEmpty =
      child !== null &&
      typeof child === "object" &&
      !Array.isArray(child) &&
      Object.keys(child).length === 0;

    if (isArrayEmpty || isObjectEmpty) {
      delete currentAsRecord[key];
    }

    return Object.keys(currentAsRecord).length === 0;
  };

  run(obj, 0);
}

/**
 * Helper for setting a value in an object via a deep path (e.g. "phones[0].number")
 *
 * @param {Record<string, RecursiveErrorNode>} obj - The object to set the key in.
 * @param {string} path - The path to set, separated by dots or array indices.
 * @param {string} value - The value to set.
 */
export function setDeep(
  obj: Record<string, RecursiveErrorNode>,
  path: string,
  value: string
): void {
  const keys = path.split(/[.[\]]+/).filter(Boolean);
  let current: RecursiveErrorNode = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    const nextKey = keys[i + 1];
    const currentAsRecord = current as Record<string, RecursiveErrorNode>;

    if (!currentAsRecord[key]) {
      currentAsRecord[key] = /^\d+$/.test(nextKey) ? [] : {};
    }
    current = currentAsRecord[key];
  }

  const lastKey = keys[keys.length - 1];
  (current as Record<string, RecursiveErrorNode>)[lastKey] = value;
}

/**
 * Helper for deeply merging errors from the client and server
 *
 * @param {RecursiveErrorNode} target - The target object to merge into.
 * @param {RecursiveErrorNode} source - The source object to merge from.
 * @returns {RecursiveErrorNode} The merged object.
 */
export function deepMergeErrors(
  target: RecursiveErrorNode,
  source: RecursiveErrorNode
): RecursiveErrorNode {
  if (typeof source !== "object" || source === null) {
    return source;
  }
  const merged = (
    Array.isArray(source)
      ? [...((target as Array<RecursiveErrorNode>) || [])]
      : { ...((target as Record<string, RecursiveErrorNode>) || {}) }
  ) as Record<string, RecursiveErrorNode>;

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceObj = source as Record<string, RecursiveErrorNode>;
      const targetObj = target as Record<string, RecursiveErrorNode>;

      const sourceValue = sourceObj[key];

      if (sourceValue && typeof sourceValue === "object") {
        merged[key] = deepMergeErrors(targetObj?.[key], sourceValue);
      } else {
        merged[key] = sourceValue;
      }
    }
  }

  return merged as RecursiveErrorNode;
}

interface Identifiable {
  id?: string | number;
}

/**
 * Check if there are remaining live errors (strings or non-empty objects)
 */
export const hasRemainingContent = (node: RecursiveErrorNode): boolean => {
  if (!node) return false;
  if (typeof node === "string") return true;
  if (Array.isArray(node)) return node.some(hasRemainingContent);
  if (typeof node === "object")
    return Object.values(node).some(hasRemainingContent);
  return false;
};

/**
 * Removes an error from an array at a specific index and cleans up the path if empty
 */
export const shiftArrayErrors = <T extends FormikValues>(
  errors: FormikErrors<T>,
  path: string,
  index: number
) => {
  const targetArray = getIn(errors, path) as RecursiveErrorNode[];

  if (Array.isArray(targetArray)) {
    targetArray.splice(index, 1);

    if (targetArray.length === 0 || !targetArray.some(hasRemainingContent)) {
      unsetDeep(errors, path);
    }
    return true;
  }
  return false;
};

/**
 * Recursive function for searching arrays in error objects that are out of sync with current values
 */
export const syncRecursiveErrors = <T extends FormikValues>(
  errorNode: RecursiveErrorNode,
  prevValues: T,
  currValues: T,
  onArrayDelete: (path: string, index: number) => void,
  path = ""
) => {
  if (!errorNode || typeof errorNode !== "object" || Array.isArray(errorNode))
    return;

  Object.keys(errorNode).forEach((key) => {
    // Form path: for arrays use [i], for objects use .
    const isNumericKey = !isNaN(Number(key));
    const currentPath = path
      ? isNumericKey
        ? `${path}[${key}]`
        : `${path}.${key}`
      : key;

    const prevData = getIn(prevValues, currentPath);
    const currData = getIn(currValues, currentPath);

    if (Array.isArray(prevData) && Array.isArray(currData)) {
      // if length has decreased — element deleted
      if (currData.length < prevData.length) {
        let deletedIndex = -1;
        for (let i = 0; i < prevData.length; i++) {
          const prevItem = prevData[i] as Identifiable | undefined;
          const currItem = currData[i] as Identifiable | undefined;

          // compare by ID (critical for correct index definition)
          if (!currItem || prevItem?.id !== currItem?.id) {
            deletedIndex = i;
            break;
          }
        }

        if (deletedIndex !== -1) {
          onArrayDelete(currentPath, deletedIndex);
        }
      } else {
        // If the length hasn't changed, we go deeper through each array element
        currData.forEach((_, idx) => {
          const nextErrorNode = (
            errorNode as Record<string, RecursiveErrorNode>
          )[key];
          if (Array.isArray(nextErrorNode) && nextErrorNode[idx]) {
            syncRecursiveErrors(
              nextErrorNode[idx],
              prevValues,
              currValues,
              onArrayDelete,
              `${currentPath}[${idx}]`
            );
          }
        });
      }
    } else {
      const nextNode = (errorNode as Record<string, RecursiveErrorNode>)[key];
      if (nextNode && typeof nextNode === "object") {
        syncRecursiveErrors(
          nextNode,
          prevValues,
          currValues,
          onArrayDelete,
          currentPath
        );
      }
    }
  });
};
